// B2B Lead Detail Page
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getLead, updateLead, getScoreClass } from '../utils/api';

const STATUS_OPTIONS = [
  { value: 'new', label: '🆕 New' },
  { value: 'contacted', label: '📞 Contacted' },
  { value: 'qualified', label: '✅ Qualified' },
  { value: 'booked', label: '🎉 Booked' },
  { value: 'lost', label: '❌ Lost' }
];

export default function B2BLeadDetail() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  
  const [lead, setLead] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('new');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    console.log('B2BLeadDetail mounted, leadId:', leadId);
    if (leadId) {
      loadLead();
    }
  }, [leadId]);

  const loadLead = async () => {
    console.log('loadLead called for:', leadId);
    setError(null);
    try {
      const data = await getLead(leadId);
      console.log('getLead response:', data);
      setLead(data.lead);
      setNotes(data.lead?.notes || '');
      setStatus(data.lead?.status || 'new');
      setConversation(data.conversation?.messages || []);
    } catch (err) {
      console.error('Failed to load lead:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateLead(leadId, { status, notes });
      // Show success feedback (could add toast here)
    } catch (err) {
      console.error('Failed to update lead:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setStatus(newStatus);
    try {
      await updateLead(leadId, { status: newStatus });
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  if (loading) {
    return (
      <div className="b2b-loading" style={{ height: '50vh' }}>
        <div className="b2b-spinner" />
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="b2b-empty-state">
        <div className="b2b-empty-state-icon">❌</div>
        <div className="b2b-empty-state-title">Lead not found</div>
        {error && <div className="b2b-empty-state-desc" style={{color: 'red'}}>Error: {error}</div>}
        <div className="b2b-empty-state-desc">Lead ID: {leadId}</div>
        <button className="b2b-btn b2b-btn-primary" onClick={() => navigate('/leads')}>
          Back to Leads
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="b2b-lead-detail-header">
        <button className="b2b-back-btn" onClick={() => navigate('/leads')}>
          ←
        </button>
        <div className="b2b-lead-detail-info">
          <h1>{lead.client_name || 'Anonymous'}</h1>
          <div className="b2b-lead-detail-meta">
            {lead.client_email && <span>{lead.client_email}</span>}
            {lead.client_phone && (
              <>
                <span>•</span>
                <span>{lead.client_phone}</span>
              </>
            )}
            <span>•</span>
            <span className={`b2b-lead-status ${status}`}>
              {STATUS_OPTIONS.find(s => s.value === status)?.label || status}
            </span>
          </div>
        </div>
        <div className="b2b-lead-detail-actions">
          {lead.client_email && (
            <a 
              href={`mailto:${lead.client_email}`} 
              className="b2b-btn b2b-btn-secondary"
            >
              📧 Email
            </a>
          )}
          {lead.client_phone && (
            <a 
              href={`tel:${lead.client_phone}`} 
              className="b2b-btn b2b-btn-secondary"
            >
              📞 Call
            </a>
          )}
          {status === 'new' && (
            <button 
              className="b2b-btn b2b-btn-success"
              onClick={() => handleStatusChange('contacted')}
            >
              ✅ Mark Contacted
            </button>
          )}
        </div>
      </div>

      {/* Content Grid */}
      <div className="b2b-lead-detail-layout">
        {/* Conversation Panel */}
        <div className="b2b-conversation-panel">
          <div className="b2b-panel-header">
            <div className="b2b-panel-title">💬 Conversation Transcript</div>
            <span style={{ fontSize: '12px', color: '#71717a' }}>
              {conversation.length} messages
            </span>
          </div>
          
          <div className="b2b-conversation-messages">
            {conversation.length === 0 ? (
              <div className="b2b-empty-state">
                <div className="b2b-empty-state-icon">💬</div>
                <div className="b2b-empty-state-title">No messages</div>
                <div className="b2b-empty-state-desc">
                  Conversation history will appear here
                </div>
              </div>
            ) : (
              conversation.map((msg, idx) => (
                <div 
                  key={idx} 
                  className={`b2b-conv-message ${msg.role === 'user' ? 'user' : 'assistant'}`}
                >
                  {msg.content}
                  {msg.timestamp && (
                    <div className="time">
                      {new Date(msg.timestamp).toLocaleTimeString([], { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Summary Panel */}
        <div className="b2b-lead-summary">
          {/* Lead Score */}
          <div className="b2b-summary-section">
            <div className="b2b-summary-title">Lead Score</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div 
                className={`b2b-lead-score ${getScoreClass(lead.score || 0)}`}
                style={{ width: '48px', height: '48px', fontSize: '18px' }}
              >
                {lead.score || 0}
              </div>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#15803d' }}>
                  {lead.score >= 70 ? 'High Quality' : 
                   lead.score >= 40 ? 'Medium Quality' : 'Low Quality'}
                </div>
                <div style={{ fontSize: '12px', color: '#71717a' }}>
                  {lead.client_email && 'Email'} 
                  {lead.client_email && lead.client_phone && ' + '}
                  {lead.client_phone && 'Phone'}
                  {lead.trip_summary?.budget && ' + Budget'}
                </div>
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="b2b-summary-section">
            <div className="b2b-summary-title">Status</div>
            <select 
              className="b2b-form-select"
              value={status}
              onChange={(e) => handleStatusChange(e.target.value)}
            >
              {STATUS_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Trip Details */}
          <div className="b2b-summary-section">
            <div className="b2b-summary-title">Trip Details</div>
            
            <div className="b2b-summary-row">
              <span className="label">Destination</span>
              <span className="value">
                {typeof lead.trip_summary?.destination === 'string' 
                  ? lead.trip_summary.destination 
                  : '-'}
              </span>
            </div>
            
            <div className="b2b-summary-row">
              <span className="label">Travel Dates</span>
              <span className="value">
                {typeof lead.trip_summary?.travel_dates === 'string' 
                  ? lead.trip_summary.travel_dates 
                  : lead.trip_summary?.travel_dates?.start 
                    ? `${lead.trip_summary.travel_dates.start}${lead.trip_summary.travel_dates.end ? ' - ' + lead.trip_summary.travel_dates.end : ''}`
                    : '-'}
              </span>
            </div>
            
            <div className="b2b-summary-row">
              <span className="label">Duration</span>
              <span className="value">{lead.trip_summary?.duration || '-'}</span>
            </div>
            
            <div className="b2b-summary-row">
              <span className="label">Travelers</span>
              <span className="value">{lead.trip_summary?.travelers || '-'}</span>
            </div>
            
            <div className="b2b-summary-row">
              <span className="label">Trip Type</span>
              <span className="value">{lead.trip_summary?.trip_type || '-'}</span>
            </div>
            
            <div className="b2b-summary-row">
              <span className="label">Budget</span>
              <span className="value">{lead.trip_summary?.budget || '-'}</span>
            </div>
            
            {lead.trip_summary?.special_requests && (
              <div className="b2b-summary-row">
                <span className="label">Special Requests</span>
                <span className="value">{lead.trip_summary.special_requests}</span>
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="b2b-summary-section">
            <div className="b2b-summary-title">Your Notes</div>
            <textarea
              className="b2b-form-textarea"
              placeholder="Add notes about this lead..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            <button 
              className="b2b-btn b2b-btn-primary"
              style={{ width: '100%', marginTop: '12px' }}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Notes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
